import { useRef } from "react";
import styled from "styled-components";
import {
  useOverlay,
  usePreventScroll,
  useModal,
  OverlayContainer,
} from "@react-aria/overlays";
import { useDialog } from "@react-aria/dialog";
import { FocusScope } from "@react-aria/focus";

import { ModalProps } from "./modal.type";

import Box from "../Box/Box";
import Button from "../Button/Button";
import Typography from "../Typography/Typography";

const ModalContainer = styled(Box)`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999;
`;

const ModalScrollContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  max-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
`;

const ModalDialog = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 32px);
  margin: 16px auto;
`;

const Card = styled.div<{ css?: string }>`
  position: relative;
  display: block;
  width: auto;
  padding: 28px;
  background-color: #fff;
  border-radius: 8px;
  z-index: 1;

  &:focus {
    outline: none;
  }
  ${(props) => props.css}
`;

const XButton = styled(Button)`
  position: absolute;
  top: 28px;
  right: 28px;

  &:focus {
    outline: solid;
  }
`;

const Modal = ({
  onClose = () => {},
  isOpen = false,
  showXButton = true,
  isDismissable = false,
  shouldCloseOnBlur = false,
  isKeyboardDismissDisabled = false,
  overlayVariant = "dark",
  children,
  role = "dialog",
  id,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  "aria-describedby": ariaDescribedby,
  ...props
}: ModalProps) => {
  const ref = useRef(null);
  const { overlayProps } = useOverlay(
    {
      isOpen,
      onClose,
      isDismissable,
      shouldCloseOnBlur,
      isKeyboardDismissDisabled,
    },
    ref
  );

  usePreventScroll();

  const modalProps = useModal();
  const { dialogProps } = useDialog(
    {
      role,
      id,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledby,
      "aria-describedby": ariaDescribedby,
    },
    ref
  );

  return (
    <OverlayContainer>
      <ModalContainer>
        {overlayVariant === "dark" && (
          <Box
            width="100vw"
            height="100vh"
            backgroundColor="rgba(0,0,0,0.5)"
          ></Box>
        )}
        {overlayVariant === "blurred" && (
          <Box
            width="100vw"
            height="100vh"
            backgroundColor="rgba(0,0,0,0.4)"
            css={`
              backdrop-filter: blur(2px);
            `}
          ></Box>
        )}
        {overlayVariant === "dark-blurred" && (
          <Box
            width="100vw"
            height="100vh"
            backgroundColor="rgba(9, 21, 53, 0.94)"
            css={`
              backdrop-filter: blur(8px);
            `}
          ></Box>
        )}
        <FocusScope contain restoreFocus>
          <ModalScrollContainer>
            <ModalDialog>
              <Card
                ref={ref}
                {...overlayProps}
                {...dialogProps}
                {...modalProps}
                {...props}
              >
                {showXButton && (
                  <XButton
                    onClick={onClose}
                    variant="tertiary"
                    m={0}
                    p={0}
                    height={16}
                  >
                    <Typography
                      m={0}
                      p={0}
                      color="black"
                      css={`
                        font-size: 30px;
                        font-weight: 100;
                        line-height: 16px;
                      `}
                    >
                      &times;
                    </Typography>
                  </XButton>
                )}
                {children}
              </Card>
            </ModalDialog>
          </ModalScrollContainer>
        </FocusScope>
      </ModalContainer>
    </OverlayContainer>
  );
};

export default Modal;
